import { useCallback, useEffect, useState } from 'react';
import { BookOpenCheck, Loader2, Presentation, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchChapterSummary } from '@/lib/chapterDigest';
import useActiveScope from '@/lib/useActiveScope';
import ChapterSummaryView from '@/components/study/ChapterSummaryView';

/**
 * Summary — the whole chapter as a readable, bullet-first revision page.
 *
 * Built once per chapter server-side and cached for everyone, so this page is a
 * plain read: there is no "generate" button and no second credit to spend.
 */
export default function SummaryPage() {
  const navigate = useNavigate();
  const { subject, topic } = useActiveScope();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!subject || !topic) return;
    setLoading(true); setError('');
    try { setData(await fetchChapterSummary(subject, topic)); }
    catch (e) { setError(e?.message || 'The summary is unavailable right now'); }
    setLoading(false);
  }, [subject, topic]);

  useEffect(() => { load(); }, [load]);

  if (!subject || !topic) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="summary-no-scope">
        <BookOpenCheck className="w-10 h-10 text-indigo-300 mx-auto" />
        <p className="font-heading text-base text-slate-800 mt-3">Pick a subject and chapter first</p>
        <p className="font-body text-sm text-slate-500 mt-1">
          Choose them in the bar at the top and I'll lay out the whole chapter for you.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="summary-page">

      <div className="flex items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
            {data?.payload?.title || topic}
          </h1>
          <p className="font-body text-sm text-slate-500 mt-1">
            {subject} · every heading of this chapter, in points you can actually remember
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => navigate('/slides')} data-testid="summary-open-slides"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900 text-white text-[11px] font-heading hover:bg-slate-800 transition-colors">
            <Presentation className="w-3.5 h-3.5" /> Slides
          </button>
          <button onClick={load} data-testid="summary-reload"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-[11px] font-heading text-slate-600 hover:border-slate-300 transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {loading && !data && (
        <p className="flex items-center gap-2 text-slate-400 font-body text-sm" data-testid="summary-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Laying out the chapter…
        </p>
      )}
      {error && !data && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50/70 p-4" data-testid="summary-error">
          <p className="font-body text-sm text-rose-700">{error}</p>
          <button onClick={load} className="mt-2 text-sm font-heading text-rose-700 underline" data-testid="summary-retry">Try again</button>
        </div>
      )}
      {data?.payload && <ChapterSummaryView summary={data.payload} />}
    </div>
  );
}
